// 三次曲线与矩阵相关的纯函数工具
// 这些函数不依赖任何响应式状态，可安全复用

// 计算 4x4 矩阵的行列式
export function det4(m) {
  return (
      m[0][3] * m[1][2] * m[2][1] * m[3][0] - m[0][2] * m[1][3] * m[2][1] * m[3][0] -
      m[0][3] * m[1][1] * m[2][2] * m[3][0] + m[0][1] * m[1][3] * m[2][2] * m[3][0] +
      m[0][2] * m[1][1] * m[2][3] * m[3][0] - m[0][1] * m[1][2] * m[2][3] * m[3][0] -
      m[0][3] * m[1][2] * m[2][0] * m[3][1] + m[0][2] * m[1][3] * m[2][0] * m[3][1] +
      m[0][3] * m[1][0] * m[2][2] * m[3][1] - m[0][0] * m[1][3] * m[2][2] * m[3][1] -
      m[0][2] * m[1][0] * m[2][3] * m[3][1] + m[0][0] * m[1][2] * m[2][3] * m[3][1] +
      m[0][3] * m[1][1] * m[2][0] * m[3][2] - m[0][1] * m[1][3] * m[2][0] * m[3][2] -
      m[0][3] * m[1][0] * m[2][1] * m[3][2] + m[0][0] * m[1][3] * m[2][1] * m[3][2] +
      m[0][1] * m[1][0] * m[2][3] * m[3][2] - m[0][0] * m[1][1] * m[2][3] * m[3][2] -
      m[0][2] * m[1][1] * m[2][0] * m[3][3] + m[0][1] * m[1][2] * m[2][0] * m[3][3] +
      m[0][2] * m[1][0] * m[2][1] * m[3][3] - m[0][0] * m[1][2] * m[2][1] * m[3][3] -
      m[0][1] * m[1][0] * m[2][2] * m[3][3] + m[0][0] * m[1][1] * m[2][2] * m[3][3]
  );
}

// 替换矩阵的某一列
export function replaceCol(m, colIdx, newCol) {
  return m.map((row, i) => row.map((v, j) => (j === colIdx ? newCol[i] : v)));
}

// 曲线公式：通过四点反推三次多项式系数
export function solveCubicCoefficients(x0, y0, x1, y1, x2, y2, x3, y3) {
  const A = [
    [Math.pow(x0, 3), Math.pow(x0, 2), x0, 1],
    [Math.pow(x1, 3), Math.pow(x1, 2), x1, 1],
    [Math.pow(x2, 3), Math.pow(x2, 2), x2, 1],
    [Math.pow(x3, 3), Math.pow(x3, 2), x3, 1]
  ];
  const Y = [y0, y1, y2, y3];

  const D = det4(A);
  if (Math.abs(D) < 1e-8) return [0, 0, 0, 0];
  const D0 = det4(replaceCol(A, 0, Y));
  const D1 = det4(replaceCol(A, 1, Y));
  const D2 = det4(replaceCol(A, 2, Y));
  const D3 = det4(replaceCol(A, 3, Y));
  return [D0 / D, D1 / D, D2 / D, D3 / D];
}

export function getCurveValue(coeffs, x) {
  if (!coeffs || coeffs.length !== 4) return 0
  const [k3, k2, k1, k0] = coeffs;
  const result = k3 * Math.pow(x, 3) + k2 * Math.pow(x, 2) + k1 * x + k0;
  return isNaN(result) || !isFinite(result) ? 0 : result;
}

// 使用最小二乘法拟合三次函数
export function fitCubicWithLeastSquares(points, maxStep) {
  if (points.length < 4) return null

  // 构建矩阵 A: [[x^3, x^2, x, 1], ...]
  const A = points.map(p => [
    Math.pow(p.step, 3),
    Math.pow(p.step, 2),
    p.step,
    1
  ])

  // 构建向量 b: [y1, y2, ...]
  const b = points.map(p => p.angle)

  // 计算 A^T * A
  const AT = [
    A.map(row => row[0]),
    A.map(row => row[1]),
    A.map(row => row[2]),
    A.map(row => row[3])
  ]

  const ATA = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0]
  ]

  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 4; j++) {
      ATA[i][j] = AT[i].reduce((sum, val, idx) => sum + val * AT[j][idx], 0)
    }
  }

  // 计算 A^T * b
  const ATb = [
    AT[0].reduce((sum, val, idx) => sum + val * b[idx], 0),
    AT[1].reduce((sum, val, idx) => sum + val * b[idx], 0),
    AT[2].reduce((sum, val, idx) => sum + val * b[idx], 0),
    AT[3].reduce((sum, val, idx) => sum + val * b[idx], 0)
  ]

  // 使用克拉默法则求解 (A^T * A) * x = A^T * b
  const det = det4(ATA)
  if (Math.abs(det) < 1e-8) return null

  const coeffs = [
    det4(replaceCol(ATA, 0, ATb)) / det,
    det4(replaceCol(ATA, 1, ATb)) / det,
    det4(replaceCol(ATA, 2, ATb)) / det,
    det4(replaceCol(ATA, 3, ATb)) / det
  ]

  // 检查系数是否有效
  if (coeffs.some(c => isNaN(c) || !isFinite(c))) {
    return null
  }

  return coeffs
}

// 格式化三次函数表达式
export function formatCubicFunction(segment) {
  const terms = []

  // k3项
  if (Math.abs(segment.k3) > 1e-8) {
    if (Math.abs(segment.k3 - 1) < 1e-8) {
      terms.push('x³')
    } else if (Math.abs(segment.k3 + 1) < 1e-8) {
      terms.push('-x³')
    } else {
      terms.push(`${segment.k3.toFixed(6)}x³`)
    }
  }

  // k2项
  if (Math.abs(segment.k2) > 1e-8) {
    const sign = segment.k2 >= 0 ? '+' : ''
    if (Math.abs(segment.k2 - 1) < 1e-8) {
      terms.push(`${sign}x²`)
    } else if (Math.abs(segment.k2 + 1) < 1e-8) {
      terms.push('-x²')
    } else {
      terms.push(`${sign}${segment.k2.toFixed(6)}x²`)
    }
  }

  // k1项
  if (Math.abs(segment.k1) > 1e-8) {
    const sign = segment.k1 >= 0 ? '+' : ''
    if (Math.abs(segment.k1 - 1) < 1e-8) {
      terms.push(`${sign}x`)
    } else if (Math.abs(segment.k1 + 1) < 1e-8) {
      terms.push('-x')
    } else {
      terms.push(`${sign}${segment.k1.toFixed(6)}x`)
    }
  }

  // k0项
  if (Math.abs(segment.k0) > 1e-8) {
    const sign = segment.k0 >= 0 ? '+' : ''
    terms.push(`${sign}${segment.k0.toFixed(6)}`)
  }

  return terms.length > 0 ? terms.join(' ') : '0'
}
